import { DEFAULT_COMPANY_ID, getCompanyConfig } from './companies';
import { getActiveCompanyId } from './session';

/**
 * Per-company terminology for on-screen text, mirroring the app's labels.
 *
 * `t('promotor')` is the session company's word for the promotor role. Without a session
 * (landing, login) or for an unknown company, Mary's words apply, the same fallback the API
 * URL and feature flags use. Keys are defined in `companies/<id>/labels.js`.
 *
 * Only display text goes through here. Role values (`userType === 'promotor'`), route paths,
 * API fields, permission keys and storage keys never change.
 *
 * The session company only changes at sign-in and logout, and both reload the page when the
 * company changes, so screens read labels at render time without subscribing.
 */

const DEFAULT_LABELS = getCompanyConfig(DEFAULT_COMPANY_ID).labels;

/**
 * Labels of `companyId`, or Mary's for an unknown company or none.
 * @param {string|null|undefined} companyId
 * @returns {Readonly<Object<string, string>>}
 */
export function labelsFor(companyId) {
    const company = getCompanyConfig(companyId);
    return (company && company.labels) || DEFAULT_LABELS;
}

/**
 * Label `key` for `companyId`: the company's, else Mary's, else the key itself.
 * @param {string|null|undefined} companyId
 * @param {string} key
 * @returns {string}
 */
export function labelFor(companyId, key) {
    const labels = labelsFor(companyId);
    if (Object.prototype.hasOwnProperty.call(labels, key)) return labels[key];
    if (Object.prototype.hasOwnProperty.call(DEFAULT_LABELS, key)) return DEFAULT_LABELS[key];
    return key;
}

/**
 * Label `key` for the session company. Works anywhere: class components, controllers,
 * Excel column names.
 * @param {string} key - e.g. 'promotor', 'promotores', 'promotorLower', 'ownBrand'
 * @returns {string}
 */
export function t(key) {
    return labelFor(getActiveCompanyId(), key);
}
